import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAnalyses } from '../analyses/AnalysesContext';
import { useAuth } from '../auth/AuthContext';

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')} min`;
}

function formatProcessing(seconds) {
  if (seconds === null || seconds === undefined) return '';
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')} min`;
}

const STAGE_LABEL = {
  queued: 'Warteschlange',
  uploading_to_google: 'Upload zum LLM',
  processing_google: 'LLM verarbeitet Datei',
  generating: 'Analyse läuft',
  finalizing: 'Abschluss',
};

function GroupItem({ a }) {
  const { remove, reanalyze } = useAnalyses();
  const { user } = useAuth();
  const canChooseModel = !!user?.can_choose_model;
  const isLive = a.status === 'queued' || a.status === 'processing';
  const pct = a._progress ?? 0;
  // Worker-Detail-Texte koennen Modellnamen/"Google" enthalten – nur fuer
  // Model-Picker-User anzeigen, sonst neutrale Stage-Labels.
  const stageLabel = (canChooseModel && a._detail)
    || STAGE_LABEL[a._stage] || STAGE_LABEL[a.status] || 'Wird verarbeitet …';
  const proc = formatProcessing(a.processing_duration_seconds);
  const linkable = a.status === 'completed';
  const [retrying, setRetrying] = useState(false);

  const onDelete = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const label = canChooseModel ? `Analyse mit ${a.model_used || a.model_requested}` : 'Diese Analyse';
    if (!window.confirm(`${label} löschen?`)) return;
    await remove(a.id);
  };

  const onReanalyze = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!window.confirm('Analyse erneut starten?')) return;
    setRetrying(true);
    try {
      await reanalyze(a.id, a.model_used || a.model_requested);
    } catch (err) {
      window.alert(err.response?.data?.error || 'Re-Analyse fehlgeschlagen');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className={`group-item${a.status === 'failed' ? ' group-item-failed' : ''}`}>
      {canChooseModel && (
        <div className="group-item-model">
          🧠 {a.model_used || a.model_requested}
        </div>
      )}
      <div className="group-item-status">
        {isLive ? (
          <div className="group-progress">
            <div className="group-bar"><div className="group-fill" style={{ width: `${pct}%` }} /></div>
            <span className="group-stage">{pct}% · {stageLabel}</span>
          </div>
        ) : linkable ? (
          <>
            {canChooseModel && proc && <span className="meta">{proc}</span>}
            <Link to={`/analyses/${a.id}`} className="group-item-title-link">
              {a.visit_title ? `„${a.visit_title}"` : 'Doku öffnen →'}
            </Link>
          </>
        ) : (
          <span className="failed" title={canChooseModel ? a.error_message : undefined}>
            ✗ {canChooseModel ? (a.error_message || 'Fehler') : 'Analyse fehlgeschlagen – bitte erneut versuchen'}
          </span>
        )}
      </div>
      <div className="group-item-actions">
        {!isLive && (
          <button
            onClick={onReanalyze}
            disabled={retrying}
            className="icon-btn"
            title="Analyse mit gleichem Modell erneut starten"
            aria-label="erneut analysieren"
          >
            {retrying ? '…' : '🔄'}
          </button>
        )}
        <button onClick={onDelete} className="icon-btn" title="Diese Analyse löschen" aria-label="löschen">
          🗑
        </button>
      </div>
    </div>
  );
}

function AddAnalysisAction({ items, allModels }) {
  const { reanalyze } = useAnalyses();
  const { user } = useAuth();
  const [picking, setPicking] = useState(false);
  const [newModel, setNewModel] = useState('');
  const [busy, setBusy] = useState(false);

  if (!user?.can_choose_model) return null; // Stilles Verstecken fuer normale User

  // Root finden – wo wir reanalyzen, hat ein vollständiges Transkript
  const root = items.find(
    (x) => !x.source_analysis_id && x.status === 'completed' && x.transcript_content
  ) || items.find((x) => x.status === 'completed' && x.transcript_content);

  if (!root) return null; // keine fertige Analyse vorhanden – nichts zu reanalyzen

  const usedModels = new Set(items.map((i) => i.model_used || i.model_requested));
  const available = allModels.filter((m) => !usedModels.has(m.id));
  if (available.length === 0) return null;

  const onSubmit = async () => {
    if (!newModel) return;
    setBusy(true);
    try {
      await reanalyze(root.id, newModel);
      setPicking(false);
      setNewModel('');
    } finally {
      setBusy(false);
    }
  };

  if (!picking) {
    return (
      <button onClick={() => setPicking(true)} className="add-analysis-btn" title="Weitere Analyse mit anderem Modell">
        + Analyse
      </button>
    );
  }

  return (
    <div className="add-analysis-picker">
      <select value={newModel} onChange={(e) => setNewModel(e.target.value)} disabled={busy} autoFocus>
        <option value="">– Modell wählen –</option>
        {available.map((m) => (
          <option key={m.id} value={m.id}>{m.label}</option>
        ))}
      </select>
      <button onClick={onSubmit} disabled={!newModel || busy} className="btn-primary-sm">
        {busy ? '…' : 'Starten'}
      </button>
      <button onClick={() => { setPicking(false); setNewModel(''); }} disabled={busy} className="btn-cancel">
        ✕
      </button>
    </div>
  );
}

function AudioButtons({ analysisId, filename, audioUrl, setAudioUrl }) {
  const [loading, setLoading] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const ensureBlob = async () => {
    if (objectUrlRef.current) return objectUrlRef.current;
    const res = await api.get(`/analyses/${analysisId}/audio`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    objectUrlRef.current = url;
    return url;
  };

  const onPlay = async () => {
    if (audioUrl) {
      setAudioUrl(null);
      return;
    }
    setLoading(true);
    try {
      setAudioUrl(await ensureBlob());
    } catch (e) {
      window.alert(e.response?.status === 404 ? 'Audio-Datei nicht verfügbar.' : 'Laden fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const onDownload = async () => {
    setLoading(true);
    try {
      const url = await ensureBlob();
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      window.alert(e.response?.status === 404 ? 'Audio-Datei nicht verfügbar.' : 'Download fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="audio-actions">
      <button onClick={onPlay} disabled={loading} className="icon-btn" title={audioUrl ? 'Player schließen' : 'Audio abspielen'}>
        {loading ? '…' : audioUrl ? '⏸' : '▶'}
      </button>
      <button onClick={onDownload} disabled={loading} className="icon-btn" title="Audio herunterladen">
        ⬇
      </button>
    </div>
  );
}

export default function AnalysisGroup({ items, showUser = false, allModels = [] }) {
  const primary = items.find((x) => !x.source_analysis_id) || items[0];
  const createdAt = new Date(primary.created_at).toLocaleString('de-DE');
  const size = formatSize(primary.audio_size_bytes);
  const duration = formatDuration(primary.audio_duration_seconds);
  const [audioUrl, setAudioUrl] = useState(null);

  return (
    <div className="analysis-group">
      <div className="group-header">
        <div className="group-header-main">
          <div className="group-headline">📄 {primary.original_filename}</div>
          <div className="meta">
            {createdAt}
            {duration && ` · ${duration}`}
            {size && ` · ${size}`}
            {showUser && primary.user && ` · 👤 ${primary.user.display_name}`}
            {items.length > 1 && ` · ${items.length} Analysen`}
          </div>
        </div>
        <div className="group-header-actions">
          <AudioButtons
            analysisId={primary.id}
            filename={primary.original_filename}
            audioUrl={audioUrl}
            setAudioUrl={setAudioUrl}
          />
          <AddAnalysisAction items={items} allModels={allModels} />
        </div>
      </div>
      {audioUrl && <audio controls autoPlay src={audioUrl} className="group-audio" />}
      <div className="group-items">
        {items.map((a) => <GroupItem key={a.id} a={a} />)}
      </div>
    </div>
  );
}
